import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useBrand } from "@/context/BrandContext";
import { formatMoney } from "@/lib/format";
import { moneyTick, colorFor, CATEGORY_COLORS } from "@/lib/charts";
import { PageHeader } from "@/components/common/PageHeader";
import { PeriodFilter } from "@/components/common/PeriodFilter";
import { StatCard } from "@/components/common/StatCard";
import { ChartCard } from "@/components/common/ChartCard";
import { Skeleton } from "@/components/ui/skeleton";

export default function CommercialDashboard() {
  const { t } = useT();
  const { brandParam } = useBrand();
  const [period, setPeriod] = useState({ preset: "this_month" });

  const { data, isLoading } = useQuery({
    queryKey: ["dash-commercial", brandParam, period.desde, period.hasta],
    queryFn: async () => (await api.get("/dashboards/commercial", {
      params: { brand: brandParam, desde: period.desde, hasta: period.hasta },
    })).data,
  });

  if (isLoading || !data) {
    return (
      <div className="p-6"><div className="grid gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[92px] rounded-[10px]" />)}
      </div></div>
    );
  }

  return (
    <>
      <PageHeader testId="dash-commercial-header" title={t("dashboards.commercial.title")}
        subtitle={t("dashboards.commercial.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6" data-testid="dash-commercial-page">
        <div className="mb-4"><PeriodFilter value={period} onChange={setPeriod} /></div>

        <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard testId="dash-com-quoted" label={t("dashboards.commercial.quoted")} value={formatMoney(data.monto_cotizado_cents)} />
          <StatCard testId="dash-com-won" label={t("dashboards.commercial.won")} value={formatMoney(data.monto_ganado_cents)} />
          <StatCard testId="dash-com-conversion" label={t("dashboards.commercial.conversion")} value={`${data.tasa_conversion_pct}%`} />
          <StatCard testId="dash-com-new-clients" label={t("dashboards.commercial.new_clients")} value={data.clientes_nuevos} />
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <ChartCard testId="dash-com-funnel" title={t("dashboards.commercial.funnel_title")} height={220} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.embudo_estimados.map((e) => (
                <tr key={e.estado} className="border-b border-line">
                  <td className="px-2 py-1">{t(`estimates.status.${e.estado}`)}</td><td className="px-2 py-1 text-right">{e.cantidad}</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.embudo_estimados.map((e) => ({ ...e, nombre: t(`estimates.status.${e.estado}`) }))}
                layout="vertical" margin={{ left: 90 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis type="category" dataKey="nombre" width={80} tick={{ fontSize: 11, fill: "var(--ink-secondary)" }} />
                <Tooltip />
                <Bar isAnimationActive={false} dataKey="cantidad" radius={4} barSize={18}>
                  {data.embudo_estimados.map((e) => <Cell key={e.estado} fill={colorFor(e.estado)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard testId="dash-com-by-seller" title={t("dashboards.commercial.by_seller")} height={Math.max(180, data.conversion_por_vendedor.length * 40)} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.conversion_por_vendedor.map((v) => (
                <tr key={v.vendedor} className="border-b border-line">
                  <td className="px-2 py-1">{v.vendedor}</td><td className="px-2 py-1 text-right">{formatMoney(v.cotizado_cents)}</td>
                  <td className="px-2 py-1 text-right">{formatMoney(v.ganado_cents)}</td><td className="px-2 py-1 text-right">{v.tasa_pct}%</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={Math.max(180, data.conversion_por_vendedor.length * 40)}>
              <BarChart data={data.conversion_por_vendedor} layout="vertical" margin={{ left: 90 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                <XAxis type="number" tickFormatter={moneyTick} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis type="category" dataKey="vendedor" width={80} tick={{ fontSize: 11, fill: "var(--ink-secondary)" }} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar isAnimationActive={false} dataKey="cotizado_cents" name="Cotizado" fill={CATEGORY_COLORS[0]} radius={4} barSize={12} />
                <Bar isAnimationActive={false} dataKey="ganado_cents" name="Ganado" fill={CATEGORY_COLORS[2]} radius={4} barSize={12} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard testId="dash-com-growth" title={t("dashboards.commercial.growth_title")} height={Math.max(200, data.crecimiento_por_compania.length * 28)} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.crecimiento_por_compania.map((c) => (
                <tr key={c.compania} className="border-b border-line">
                  <td className="px-2 py-1">{c.compania}</td><td className="px-2 py-1 text-right">{c.variacion_pct ?? "—"}%</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={Math.max(200, data.crecimiento_por_compania.length * 28)}>
              <BarChart data={data.crecimiento_por_compania} layout="vertical" margin={{ left: 110 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} unit="%" />
                <YAxis type="category" dataKey="compania" width={100} tick={{ fontSize: 10.5, fill: "var(--ink-secondary)" }} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar isAnimationActive={false} dataKey="variacion_pct" radius={4} barSize={14}>
                  {data.crecimiento_por_compania.map((c) => (
                    <Cell key={c.compania} fill={(c.variacion_pct || 0) >= 0 ? CATEGORY_COLORS[0] : "var(--state-critical)"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard testId="dash-com-ranking" title={t("dashboards.commercial.ranking_title")} height={Math.max(200, data.ranking_companias_anual.length * 28)} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.ranking_companias_anual.map((c) => (
                <tr key={c.compania} className="border-b border-line">
                  <td className="px-2 py-1">{c.compania}</td><td className="px-2 py-1 text-right">{formatMoney(c.facturacion_cents)}</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={Math.max(200, data.ranking_companias_anual.length * 28)}>
              <BarChart data={data.ranking_companias_anual} layout="vertical" margin={{ left: 110 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                <XAxis type="number" tickFormatter={moneyTick} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis type="category" dataKey="compania" width={100} tick={{ fontSize: 10.5, fill: "var(--ink-secondary)" }} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Bar isAnimationActive={false} dataKey="facturacion_cents" fill={CATEGORY_COLORS[0]} radius={4} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </>
  );
}
